import { DatePipe, formatDate } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { SettingsService } from '@core';
import { GridCriteriaModel } from '@core/services/lookup/grid-criteria.model';
import { UserPreferences } from '@core/services/user/user.model';
import { WorkQueueDetails } from '@core/services/work-queue//work-queue.model';
import { ExcelExportData } from '@progress/kendo-angular-excel-export';
import { FilterService, GridComponent } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, DataResult, FilterDescriptor, isCompositeFilterDescriptor, process, SortDescriptor, State } from '@progress/kendo-data-query';
import { GridService } from '@core/services/lookup/grid.service';
import { UserService } from '@core/services/user/user.service';
import * as DateTimeFomatter from '@shared/utils/datetime-api-post-formatter';
import { NgxSpinnerService } from 'ngx-spinner';
import { ColumnSettings } from './grid-settings/column-settings.interface';
import {
  FeatureComponentGridSettings,
  GridSettings
} from './grid-settings/gird-settings.interface';

@Component({
  selector: 'Teranet-CustomizedGrid',
  templateUrl: './customized-grid.component.html',
  styleUrls: ['./customized-grid.component.scss'],
})
export class CustomizedGridComponent implements OnInit, OnDestroy {
  @Input() clearFiltersBtn?: boolean = true;
  @Input() data?: any;
  @Input() featureComponentGridSettings?: FeatureComponentGridSettings;
  @Input() filterService: FilterService;
  @Input() gridName: string = '';
  @Input() gridSettings: GridSettings;
  @Input() initialGridSettings: GridSettings;
  @Input() isCopy?: boolean = false;
  @Input() isReject?: boolean = false;
  @Input() isCancel?: boolean = false;
  @Input() isComplete?: boolean = false;
  @Input() isAccept?: boolean = false;
  @Input() isExportExcel?: boolean = true;

  @Input() isFinishLoading: boolean = false;

  @Input() kendoGridToolbar: boolean = true;
  @Input() multiSelections?: { [key: string]: string[]; } = {};
  @Input() noRecordsMessage: string = '';
  @Input() pageName: string = '';
  @Input() assignedStatusId: number = -1;
  @Input() workqueueStatusId: number = -1;
  @Input() queue?: string = '';
  @Input() resetBtn?: boolean = true;
  @Input() serverPagination?: boolean = false;
  @Output() onCopy = new EventEmitter<number>(true);
  @Output() onDelete = new EventEmitter<number>(true);
  private dateFilterSettings: ColumnSettings[];
  public excelColumnSettings: ColumnSettings[];
  public iconColumnSettings: ColumnSettings[];
  public iconColumnWidth: number = 0;
  public filtered: boolean = false;
  public selected: number = -1;
  public spinnerId: number = 0;

  @ViewChild(GridComponent) grid: GridComponent;

  constructor(
    private datePipe: DatePipe,
    private gridService: GridService,
    private spinner: NgxSpinnerService,
    private settingsService: SettingsService,
    private userService: UserService
  ) {
    this.allData = this.allData.bind(this);
    this.settingsService.loadedChanged$.subscribe(value => {
      if (value) {
        this.formatData();
      }
    });
    this.settingsService.userPreferenceChanged$.subscribe(value => {
      if (value) {
        this.activeFilter();
      }
    });
  }

  ngOnInit(): void {
    this.refreshColumnSettings();
    this.checking();
  }

  ngOnDestroy(): void {
    this.spinner.hide(`spinner${this.spinnerId}`);
  }

  private activeFilter(): void {
    this.filtered = JSON.stringify(this.gridSettings?.state?.filter?.filters) != JSON.stringify(this.initialGridSettings?.state?.filter?.filters);
  }

  public allData(): ExcelExportData {
    return {
      data: process(this.data ? this.data : this.gridSettings.gridData.data, {
        filter: this.gridSettings.state.filter,
        sort: this.gridSettings.state.sort,
        group: this.gridSettings.state.group,
      }).data,
    };
  }

  public checkCopyDisabled(): boolean {
    if (!this.gridSettings.gridData.data || this.gridSettings.gridData.data?.length === 0 || this.selected < 0) {
      this.selected = -1;
      return true;
    }
    return false;
  }

  private checking(): void {
    setTimeout(() => {
      this.grid.scrollTo({ column: 0 });
    }, 100);
    this.activeFilter();
  }

  private formatData(): void {
    if (this.dateFilterSettings?.length > 0) {
      this.dateFilterSettings.forEach(item => {
        if (this.gridSettings?.gridData?.data?.length > 0) {
          this.gridSettings.gridData.data.map(d => {
            if (d[item.field]) {
              let strDatetime: string;
              if (item.type === 'dateUTC') {
                strDatetime = this.datePipe.transform((new Date(this.datePipe.transform(d[item.field], "yyyy-MM-dd HH:mm:ss") + " UTC")), item.format);
              }
              else {
                strDatetime = this.datePipe.transform((new Date(this.datePipe.transform(d[item.field], "yyyy-MM-dd HH:mm:ss"))).toISOString(), item.format);
              }
              d[item.field] = new Date(this.datePipe.transform(strDatetime, "yyyy-MM-dd HH:mm:ss"));
            }
          })
        }
      });
    }

    if (this.gridSettings?.state?.filter?.filters?.length > 0) {
      this.gridSettings.state.filter.filters.map(f => (isCompositeFilterDescriptor(f) ? f.filters : [f]).map((current: FilterDescriptor) => {
        if (current && DateTimeFomatter.isIsoDate(current.value)) {
          current.value = new Date(current.value);
        }
      }));
    }

    if (!this.serverPagination && this.gridSettings?.state) {
      let data: any[] = this.gridSettings?.gridData?.data ?? [];
      let gridData: DataResult = process(data, this.gridSettings.state);
      this.gridSettings.gridData = { data: gridData.data, total: data.length };
    }
  }

  public excelFileName(): string {
    return this.gridName + '_' + formatDate(new Date(), 'yyyyMMdd', 'en') + '.xlsx';
  }

  public getNgClass(dataItem, okStatus: string): string {
    return dataItem === okStatus ? 'focus' : 'error'
  }

  public getPrimitiveMultiSelect(fieldName: string): any {
    if (Object.keys(this.multiSelections)?.length > 0) {
      return [...new Set(Object.entries(this.multiSelections).filter(item => item[0] == fieldName).map(item => item[1])[0])];
    }
    else {
      return this.data ? [...new Set(this.data.map(item => item[fieldName]))] :
        [...new Set(this.gridSettings.gridData.data.map(item => item[fieldName]))];
    }
  }

  public getQueryParams(dataItem, routerLink: string): any {
    if (routerLink === '/accountview' && this.pageName === 'lookup') {
      return {
        id: dataItem.id,
        previous_page: this.pageName,
        queue: null
      }
    }
    else if (routerLink === '/accountview' && this.pageName === 'workqueue') {
      return {
        id: dataItem.id,
        assignedStatusId: this.assignedStatusId,
        workqueueStatusId: this.workqueueStatusId,
        previous_page: this.pageName,
        queue: this.queue !== null && this.queue.length !== 0 ? this.queue : null,
      }
    }
    else if (routerLink === '/edituser' || routerLink === '/role') {
      return {
        id: dataItem.id
      }
    }
    else if (routerLink === '/userpreviewannouncement') {
      return {
        additional_id: dataItem.additionalId,
        id: dataItem.id,
        title: dataItem.displayName
      }
    }
    else {
      return {
        id: dataItem.id,
        title: dataItem.displayName
      }
    }
  }

  public onClearFilter() {
    if (this.gridSettings.state.sort) {
      this.gridSettings.state.sort.forEach(sort => {
        sort.dir = undefined;
      });
    }
    this.gridSettings.state.filter = JSON.parse(JSON.stringify(this.initialGridSettings.state.filter)) as CompositeFilterDescriptor;
    this.gridSettings.state.sort = JSON.parse(JSON.stringify(this.initialGridSettings.state.sort ?? [])) as SortDescriptor[];
    this.gridSettings.state.skip = 0;
    this.selected = -1;
    this.onDataStateChange(this.gridSettings.state);
  }

  public onCopyEvent(selected: number) {
    // call function of parent component
    this.onCopy.emit(selected);
  }

  public onDataStateChange(state: State): void {
    this.gridSettings.state = state;
    if (!this.serverPagination && this.data) {
      this.gridSettings.gridData = process(this.data, this.gridSettings.state);
    }
    this.saveData();
  }

  public onDeleteEvent(item) {
    // call function of parent component
    this.onDelete.emit(item);
  }

  public onExportToExcel(): void {
    this.spinnerId = Date.now();
    this.spinner.show(`spinner${this.spinnerId}`);

    const total: number = this.gridSettings.gridData.total + 1;

    const gridCriteria: GridCriteriaModel = {
      customizedSetting: this.featureComponentGridSettings?.componentGridSettings?.find(
        p => p.componentName === this.featureComponentGridSettings.featureName)?.customizedSettings,
      key: this.queue,
      gridSetting: JSON.stringify(this.gridSettings, function replacer(key, value) {
        if (key === 'gridData') {
          return { data: [], total: 0 };
        } else if (key === 'state') {
          return {
            skip: 0,
            take: total,
            sort: value.sort,
            filter: value.filter,
            group: value.group,
          };
        }
        else {
          return value
        }
      })
    };

    this.gridService.refreshGridData(gridCriteria).subscribe({
      next: (value) => {
        let gridData: DataResult = value as any;

        //gridData.data.forEach(value => {
        //  if (value) {
        //    (value as WorkQueueDetailModel).psaDt = !value.psaD ? null : new Date(value.psaD);
        //  };
        //});

        const data = this.data;

        this.data = process(gridData.data, {
          filter: this.gridSettings.state.filter,
          sort: this.gridSettings.state.sort,
          group: this.gridSettings.state.group,
        }).data;

        this.grid.saveAsExcel();

        this.data = data;

        this.spinner.hide(`spinner${this.spinnerId}`);
      }, error: () => {
        this.spinner.hide(`spinner${this.spinnerId}`);
      }
    });
  }

  public onPageChange(e: any): void {
    this.gridSettings.state.skip = e.skip;
  }

  public onReorder(e: any): void {
    const reorderedColumn = this.gridSettings.columnsConfig.splice(e.oldIndex, 1);
    this.gridSettings.columnsConfig.splice(e.newIndex, 0, ...reorderedColumn);
    this.saveData();
  }

  public onResetColumn() {
    this.gridSettings.columnsConfig = JSON.parse(JSON.stringify(this.initialGridSettings.columnsConfig)) as ColumnSettings[];
    this.gridSettings.state = JSON.parse(JSON.stringify(this.initialGridSettings.state)) as State;
    this.gridSettings.state.filter = JSON.parse(JSON.stringify(this.initialGridSettings.state.filter)) as CompositeFilterDescriptor;

    this.refreshColumnSettings();

    if (this.gridSettings.state.sort) {
      this.gridSettings.state.sort.forEach(sort => {
        sort.dir = undefined;
      });
      this.gridSettings.state.sort = JSON.parse(JSON.stringify(this.initialGridSettings.state.sort ?? [])) as SortDescriptor[];
    }

    this.gridSettings.state.skip = 0;
    this.selected = -1;

    this.onDataStateChange(this.gridSettings.state);
  }

  public onResize(e: any): void {
    e.forEach(item => {
      this.gridSettings.columnsConfig.find(col => col.field === item.column.field).width =
        item.newWidth;
    });
    this.saveData(true);
  }

  public onSelectedEvent(dataItem) {
    this.selected = dataItem.id;
  }

  public onVisibilityChange(e: any): void {
    e.columns.forEach(column => {
      this.gridSettings.columnsConfig.find(col => col.field === column.field).hidden = column.hidden;
    });
    this.saveData();
  }

  private refreshColumnSettings() {
    this.dateFilterSettings = this.gridSettings.columnsConfig.filter(c => c?.filter === 'date' ?? true);
    this.excelColumnSettings = this.gridSettings.columnsConfig.filter(c => !c?.excludeFromExcel ?? true);
    this.iconColumnSettings = this.gridSettings.columnsConfig.filter(c => c?.type === 'icon' ?? true);
    this.iconColumnWidth = this.iconColumnSettings.reduce((n, { width }) => n + width, 0);
    this.gridSettings.columnsConfig = this.gridSettings.columnsConfig.filter(c => !(c?.type === 'icon') ?? true);
  }

  private refreshGridData() {
    if (this.serverPagination) {
      this.spinnerId = Date.now();
      this.spinner.show(`spinner${this.spinnerId}`);

      const gridCriteria: GridCriteriaModel = {
        customizedSetting: this.featureComponentGridSettings?.componentGridSettings?.find(
          p => p.componentName === this.featureComponentGridSettings.featureName)?.customizedSettings,
        key: this.queue,
        gridSetting: JSON.stringify(this.gridSettings, function replacer(key, value) {
          if (key === 'gridData') {
            return { data: [], total: 0 };
          }
          else {
            return value;
          }
        })
      };

      this.gridService.refreshGridData(gridCriteria).subscribe({
        next: (value) => {
          let gridData: DataResult = value as any;

          //gridData.data.forEach(value => {
          //  if (value) {
          //    (value as WorkQueueDetailModel).psaDt = !value.psaD ? null : new Date(value.psaD);
          //  };
          //});

          this.gridSettings.gridData = { data: gridData.data, total: gridData.total };
          this.gridService.updated.next(gridCriteria.key?.toString());
          this.spinner.hide(`spinner${this.spinnerId}`);
        }, error: () => {
          this.spinner.hide(`spinner${this.spinnerId}`);
        }
      });
    }
  }

  private saveData(noRefreshing?: boolean): void {
    if (!noRefreshing) {
      this.refreshGridData();
    }
    this.saveUserPreference();
    this.checking();
  }

  private saveUserPreference(): void {
    if (this.featureComponentGridSettings?.componentGridSettings?.length > 0) {
      this.featureComponentGridSettings.componentGridSettings
        .find(p => p.componentName === this.featureComponentGridSettings.featureName).gridSettings[0] =
        JSON.parse(JSON.stringify(this.gridSettings, function replacer(key, value) {
          if (key === 'gridData') {
            return { data: [], total: 0 };
          }
          else {
            return value;
          }
        }));

      const userPreferences: UserPreferences = {
        dashboardComponentName: this.featureComponentGridSettings.routerName,
        dashboardPreferences: JSON.stringify(this.featureComponentGridSettings.componentGridSettings)
      };

      this.userService.createOrUpdateUserDashboardPreferences(userPreferences).subscribe();
    }
  }
}
